import StyledButton from '@/components/styled/StyledButton';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import CloseIcon from '@/assets/icons/close.svg?react';
import { useTranslation } from '@/i18n';

interface StyledCircuitModalProps {
  closeModal: () => void;
  title: string;
  actionTitle: string;
  actionOnClick: () => void;
  message?: string;
  isActionDisabled?: boolean;
  dialogClassName?: string;
}

const StyledCircuitModal = ({
  closeModal,
  title,
  message,
  actionTitle,
  actionOnClick,
  children,
  isActionDisabled = false,
  dialogClassName,
}: PropsWithChildren<StyledCircuitModalProps>) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();

  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      if (dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const { clientX: x, clientY: y } = event;

        if (
          x < rect.left ||
          x > rect.right ||
          y < rect.top ||
          y > rect.bottom
        ) {
          closeModal();
        }
      }
    },
    [closeModal],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <dialog
      ref={dialogRef}
      open
      className={`absolute left-auto top-0 z-50 min-h-full w-1/3 bg-white border border-[#E2E8F0] ${dialogClassName ?? ''}`}
    >
      <div className="flex flex-col justify-between w-full gap-8 p-6 rounded-md">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h1 className="text-lg leading-6 font-semibold text-gray-950">
              {title}
            </h1>
            <StyledTooltipContainer tooltipMessage={t('ui.tooltip.close')}>
              <CloseIcon className="size-5 fill-black" onClick={closeModal} />
            </StyledTooltipContainer>
          </div>
          {message && (
            <p className="text-sm leading-6 text-gray-800 max-w-full">
              {message}
            </p>
          )}
          {children}
        </div>
        <div className="flex justify-end gap-3 w-full sticky bottom-4 bg-white">
          <StyledButton
            variant="outlined"
            title={t('common.clearSelection')}
            className="text-gray-800 border-gray-300 w-fit"
            onClick={closeModal}
          />
          <StyledButton
            title={actionTitle}
            disabled={isActionDisabled}
            onClick={() => {
              actionOnClick();
              closeModal();
            }}
            className="w-fit"
          />
        </div>
      </div>
    </dialog>
  );
};

export default StyledCircuitModal;
